import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {xor, includes} from 'lodash';
import {i18n} from '../../Utility/Format';
import {getCcyPairsFromParameters} from '../../Utility/CurrencyPairHelper';
import Button from '../../Components/Controls/Button';
import Icon from '../../Components/Helper/Icon';
import Checkbox from '../../Components/Controls/Checkbox';
import CurrencyPairOptions from './CurrencyPairOptions';

const ALL_GROUPS = ["majors", "europe", "emea", "g7", "apac", "nordic", "mena", "americas"];

export default class SpotOptions extends Component {
    static propTypes = {
        currencies: PropTypes.array,
        options: PropTypes.object.isRequired,

        onCcyPairsChange: PropTypes.func.isRequired,
        onGroupsChange: PropTypes.func.isRequired,
        onMenuToggle: PropTypes.func.isRequired,
        onReset: PropTypes.func.isRequired,
        locale: PropTypes.string.isRequired,
    };

     componentDidMount() { 
  this.observer = new MutationObserver(() => {
    this.removeAriaRoles();
    this.fixRcCalendarRoles();
    this.fixInvalidTabIndex();
    this.fixMissingRoles();
});
 
  this.observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}
 
componentWillUnmount() {
  if (this.observer) {
    this.observer.disconnect();
  }
}

fixInvalidTabIndex = () => {
  // ❌ Remove tabindex from main calendar container
  document.querySelectorAll('.rc-calendar-calendar').forEach(el => {
    el.removeAttribute('tabindex');
  });
 
  // ❌ Remove from table wrapper if present
  document.querySelectorAll('.rc-calendar-table').forEach(el => {
    el.removeAttribute('tabindex');
  });
 
  // ❌ Remove from any non-clickable wrapper divs
  document.querySelectorAll('div[tabindex="0"]').forEach(el => {
    // Keep only actual date cells
    if (!el.classList.contains('rc-calendar-date')) {
      el.removeAttribute('tabindex');
    }
  });
};
 
removeAriaRoles = () => {
  document.querySelectorAll(`
    [role="grid"],
    [role="row"],
    [role="gridcell"],
    [role="table"],
    [role="columnheader"],
    [role="rowgroup"]
  `).forEach(el => {
    el.removeAttribute('role');
  });
};

fixMissingRoles = () => {
  document.querySelectorAll('[tabindex="0"]').forEach(el => {
    const tag = el.tagName.toLowerCase();
 
    // Skip native elements (already accessible)
    if (tag === 'button') return;
 
    // If it's <a> without href → needs role
    if (tag === 'a' && !el.getAttribute('href')) {
      el.setAttribute('role', 'button');
      return;
    }
 
    // If it's calendar date
    if (el.classList.contains('rc-calendar-date')) {
      el.setAttribute('role', 'button');
      return;
    }
 
    // If it's navigation or selectors
    if (
      el.className.includes('rc-calendar-prev') ||
      el.className.includes('rc-calendar-next') ||
      el.className.includes('rc-calendar-month') ||
      el.className.includes('rc-calendar-year')
    ) {
      el.setAttribute('role', 'button');
    }
  });
};

fixRcCalendarRoles = () => {
  // ❌ Remove wrong role from main container
  document.querySelectorAll('.rc-calendar-calendar').forEach(el => {
    el.removeAttribute('role');
  });
 
  // ❌ Also remove from table wrapper if exists
  document.querySelectorAll('.rc-calendar-table').forEach(el => {
    el.removeAttribute('role');
  });
 
  // ✅ Add role ONLY to actual date cells
  document.querySelectorAll('.rc-calendar-date').forEach(el => {
    el.setAttribute('role', 'button');
  });
 
  // ✅ Fix navigation buttons (already <a>, just ensure role)
  document.querySelectorAll(
    '.rc-calendar-prev-month-btn, .rc-calendar-next-month-btn, .rc-calendar-prev-year-btn, .rc-calendar-next-year-btn'
  ).forEach(el => {
    el.setAttribute('role', 'button');
  });
};

    render() {
        const addCurrencyPairOption = () => {
            const newCcyPair = this.props.options.ccyPairs[0] || 'EUR_USD';
            const changedCcyPairs = this.props.options.ccyPairs.concat([newCcyPair]);
            this.props.onCcyPairsChange(changedCcyPairs);
        };

        const changeCurrencyPairOption = (index, status, newValue) => {
            const ccyPair = this.props.options.ccyPairs[index];
            const changedCcyPairs = this.props.options.ccyPairs.slice();

            if(status === 'base') {
                changedCcyPairs[index] = newValue.isoString + ccyPair.substr(3);
            } else {
                changedCcyPairs[index] = ccyPair.substr(0,4) +  newValue.isoString;
            }

            this.props.onCcyPairsChange(changedCcyPairs);
        };

        const removeCurrencyPairOption = (index) => {
            const changedCcyPairs = this.props.options.ccyPairs.slice();
            changedCcyPairs.splice(index, 1);
            this.props.onCcyPairsChange(changedCcyPairs);
        };

        const toggleGroup = (group) => {
            const changedGroups = xor(this.props.options.groups, [group]);
            this.props.onGroupsChange(changedGroups);
        };

        const toggleMenu = (e) => {
            this.props.onMenuToggle(e, this.props.options);
        };

        const resetOptions = () => {
            this.props.onReset();
        };
        return (
            <div>
                <section>
                    <p>{i18n('dbm.spot.options.addNewCurrencyPair')}</p>
                    <CurrencyPairOptions
                        currencyPairs={getCcyPairsFromParameters(this.props.options.ccyPairs, this.props.currencies)}
                        currencies={this.props.currencies}
                        onChangeCurrencyPair={changeCurrencyPairOption}
                        onRemoveCurrencyPair={removeCurrencyPairOption}
                        showRemoveButton={true}
                        locale={this.props.locale}/>
                    <Button onClick={addCurrencyPairOption}>
                        <Icon name="plus" isCollapsed={true}/> {i18n('dbm.spot.options.addNewCurrencyPairButton')}
                    </Button>
                </section>

                <section>
                    <p>{i18n('dbm.spot.options.addNewGroups')}</p>
                    {
                        ALL_GROUPS.map((group, index) => {
                            return <Checkbox key={`option-${index}`} isSelected={includes(this.props.options.groups, group)}
                                             label={i18n(`dbm.spot.options.groups.${group}`)} onClick={() => {
                                toggleGroup(group)
                            }}/>
                        })
                    }
                    <div className="u-clearer"></div>
                </section>
                <div className="drawerPanel__panel__content--footer">
                    <Button
                        onClick={(e) => toggleMenu(e)}>
                        <Icon name="check-lg" isCollapsed={true}/> {i18n('dbm.spot.options.saveOptionsButton')}
                    </Button>
                    <Button
                        className="button button--borderless"
                        onClick={resetOptions}>
                        <Icon name="reset" isCollapsed={true}/> {i18n('dbm.spot.options.resetOptionsButton')}
                    </Button>
                </div>
            </div>
        );
    }
};